// src/App.jsx
import { useEffect, useRef, useState, useCallback } from "react";
import "./styles/global.css";

import { AuthProvider } from "./context/AuthContext";
import { useAuth }      from "./context/AuthContext";
import { useToast }     from "./hooks/useToast";
import { API }          from "./utils/constants";
import { canAccessDashboard } from "./utils/permissions";
import {
  FORUM_ORIGIN,
  MAIN_ORIGIN,
  MINECRAFT_ORIGIN,
  STORE_ORIGIN,
  isForumDomain,
  isMinecraftDomain,
  isStoreDomain,
} from "./utils/siteUrls";

import Navbar             from "./components/Navbar";
import Hero               from "./components/Hero";
import NewsFeed           from "./components/NewsFeed";
import Sidebar            from "./components/Sidebar";
import FeaturesSection    from "./components/FeaturesSection";
import MinecraftSection   from "./components/MinecraftSection";
import PodcastSection     from "./components/PodcastSection";
import DiscordSection     from "./components/DiscordSection";
import Footer             from "./components/Footer";
import Toast              from "./components/Toast";
import AuthModal          from "./components/AuthModal";
import NewsPostModal      from "./components/NewsPostModal";
import AdminDashboard     from "./components/AdminDashboard";
import StoreModal         from "./components/StoreModal";
import MinecraftStorePage from "./components/MinecraftStorePage";
import MinecraftPage      from "./components/MinecraftPage";
import MinecraftApplicationPage from "./components/MinecraftApplicationPage";
import SettingsModal      from "./components/SettingsModal";
import ForumPage          from "./components/ForumPage";
import ClothingStorePage  from "./components/ClothingStorePage";
import CharityFundPage    from "./components/CharityFundPage";

function parsePath() {
  const rawPath = window.location.pathname.replace(/\/+$/, "") || "/";
  const onForumDomain = isForumDomain();
  const onMinecraftDomain = isMinecraftDomain();
  const onStoreDomain = isStoreDomain();

  // Treat each first-class subdomain as its own application root. This is
  // intentionally resolved before the legacy main-site routes so the same
  // React bundle cannot accidentally fall back to the normal home page.
  if (onForumDomain) {
    if (rawPath === "/") {
      return {
        page: "forum", tab: null, modal: null, newsSlug: null,
        forumCat: null, forumSubcat: null, forumThread: null,
      };
    }

    const threadMatch = rawPath.match(/^\/thread\/([^/]+)$/);
    if (threadMatch) {
      return {
        page: "forum", tab: null, modal: null, newsSlug: null,
        forumCat: null, forumSubcat: null,
        forumThread: decodeURIComponent(threadMatch[1]),
      };
    }

    if (rawPath === "/archives") {
      return {
        page: "forum", tab: null, modal: null, newsSlug: null,
        forumCat: null, forumSubcat: null, forumThread: null,
      };
    }

    const sectionMatch = rawPath.match(/^\/(information|community|minecraft)(?:\/([^/]+))?$/);
    if (sectionMatch) {
      const cat = sectionMatch[1];
      const slug = sectionMatch[2] || null;
      const subcatMap = {
        information: { announcements: "announcements", rules: "rules", resources: "general-guides" },
        community: { "general-discussion": "general-discussion", fitness: "fitness", religion: "religion" },
        minecraft: { updates: "mc-updates", guides: "mc-guides", suggestions: "suggestions", factions: "factions" },
      };
      const subcat = slug ? (subcatMap[cat]?.[slug] || null) : null;
      if (!slug || subcat) {
        return {
          page: "forum", tab: null, modal: null, newsSlug: null,
          forumCat: cat, forumSubcat: subcat, forumThread: null,
        };
      }
    }

    // Unknown forum-subdomain paths still stay inside the forum rather than
    // rendering the main website.
    return {
      page: "forum", tab: null, modal: null, newsSlug: null,
      forumCat: null, forumSubcat: null, forumThread: null,
    };
  }

  if (onMinecraftDomain) {
    if (rawPath === "/" || rawPath === "/minecraft") {
      return { page: "minecraft", tab: null, modal: null, newsSlug: null };
    }

    if (rawPath === "/store" || rawPath === "/store/ranks") {
      return { page: "mcstore", tab: "ranks", modal: null, newsSlug: null };
    }

    if (rawPath === "/store/coins") {
      return { page: "mcstore", tab: "coins", modal: null, newsSlug: null };
    }

    if (rawPath === "/apply") {
      return { page: "mcapply", tab: null, modal: null, newsSlug: null };
    }

    const mcMatch = rawPath.match(/^\/(about|rules|map|factions|stats|leaderboard|mods)$/);
    if (mcMatch) {
      const tab = mcMatch[1] === "leaderboard" ? "stats" : mcMatch[1];
      return { page: "minecraft", tab, modal: null, newsSlug: null };
    }

    // Any unknown path on minecraft.theonesabove.com should remain in the
    // Minecraft experience, never drop back to the normal website homepage.
    return { page: "minecraft", tab: null, modal: null, newsSlug: null };
  }

  if (onStoreDomain) {
    // The merch storefront is the subdomain root. Keep /merch as a harmless
    // legacy fallback until Nginx has redirected all old links.
    return { page: "merch", tab: null, modal: null, newsSlug: null };
  }

  const path = rawPath;

  if (path === "/store") {
    return { page: "home", tab: null, modal: "store", newsSlug: null };
  }

  if (path === "/store/ranks") {
    return { page: "mcstore", tab: "ranks", modal: null, newsSlug: null };
  }

  if (path === "/store/coins") {
    return { page: "mcstore", tab: "coins", modal: null, newsSlug: null };
  }

  if (path === "/minecraft") {
    return { page: "minecraft", tab: null, modal: null, newsSlug: null };
  }

  if (path === "/forum") {
    return {
      page: "forum", tab: null, modal: null, newsSlug: null,
      forumCat: null, forumSubcat: null, forumThread: null,
    };
  }

  const forumThreadMatch = path.match(/^\/forum\/thread\/([^/]+)$/);
  if (forumThreadMatch) {
    return {
      page: "forum", tab: null, modal: null, newsSlug: null,
      forumCat: null, forumSubcat: null,
      forumThread: decodeURIComponent(forumThreadMatch[1]),
    };
  }

  const forumSectionMatch = path.match(/^\/forum\/(information|community|minecraft)(?:\/([^/]+))?$/);
  if (forumSectionMatch) {
    const cat = forumSectionMatch[1];
    const slug = forumSectionMatch[2] || null;
    const subcatMap = {
      information: { announcements: "announcements", rules: "rules", resources: "general-guides" },
      community: { "general-discussion": "general-discussion", fitness: "fitness", religion: "religion" },
      minecraft: { updates: "mc-updates", guides: "mc-guides", suggestions: "suggestions", factions: "factions" },
    };
    const subcat = slug ? (subcatMap[cat]?.[slug] || null) : null;
    if (!slug || subcat) {
      return {
        page: "forum", tab: null, modal: null, newsSlug: null,
        forumCat: cat, forumSubcat: subcat, forumThread: null,
      };
    }
  }

  if (path === "/forum/archives") {
    return {
      page: "forum", tab: null, modal: null, newsSlug: null,
      forumCat: null, forumSubcat: null, forumThread: null,
    };
  }

  if (path === "/merch") {
    return { page: "merch", tab: null, modal: null, newsSlug: null };
  }

  if (path === "/charity-fund" || path === "/charity-fund" || path === "/charity") {
    return { page: "charity", tab: null, modal: null, newsSlug: null };
  }

  if (path === "/minecraft/apply") {
    return { page: "mcapply", tab: null, modal: null, newsSlug: null };
  }

  const mcTabMatch = path.match(/^\/minecraft\/(.+)$/);
  if (mcTabMatch) {
    const t = mcTabMatch[1] === "leaderboard" ? "stats" : mcTabMatch[1];
    return { page: "minecraft", tab: t, modal: null, newsSlug: null };
  }

  const newsMatch = path.match(/^\/news\/(.+)$/);
  if (newsMatch) {
    return { page: "home", tab: null, modal: null, newsSlug: newsMatch[1] };
  }

  return { page: "home", tab: null, modal: null, newsSlug: null };
}

function nav(path) {
  window.history.pushState(null, "", path);
}

const FORUM_SUBCAT_SLUGS = {
  information: { announcements: "announcements", rules: "rules", "general-guides": "resources" },
  community: { "general-discussion": "general-discussion", fitness: "fitness", religion: "religion" },
  minecraft: { "mc-updates": "updates", "mc-guides": "guides", suggestions: "suggestions", factions: "factions" },
};

function forumPath(cat = null, subcat = null, thread = null) {
  if (thread) return `/thread/${thread}`;
  if (!cat) return "/";
  if (!subcat) return `/${cat}`;
  const slug = FORUM_SUBCAT_SLUGS[cat]?.[subcat] || subcat;
  return `/${cat}/${slug}`;
}

function AppInner() {
  const {
    loading: authLoading,
    authUser,
    accessToken,
    logout,
  } = useAuth();

  const {
    toast,
    showToast,
  } = useToast();

  const initial = parsePath();

  const [page, setPage] = useState(
    initial.page
  );

  const [mcStoreTab, setMcStoreTab] =
    useState(
      initial.tab || "ranks"
    );

  const MINECRAFT_TABS = new Set([
    "about",
    "rules",
    "map",
    "factions",
    "stats",
    "mods",
  ]);

  const [mcPageTab, setMcPageTab] =
    useState(
      initial.page === "minecraft" &&
      initial.tab &&
      MINECRAFT_TABS.has(initial.tab)
        ? initial.tab
        : "about"
    );

  const [storeModal, setStoreModal] =
    useState(
      initial.modal === "store"
    );

  const [authModal, setAuthModal] =
    useState(null);

  const [adminOpen, setAdminOpen] =
    useState(false);

  const [
    settingsOpen,
    setSettingsOpen,
  ] = useState(false);

  const [
    newsPosts,
    setNewsPosts,
  ] = useState([]);

  const [
    newsLoading,
    setNewsLoading,
  ] = useState(true);

  const [cart, setCart] =
    useState([]);

  const [
    activeNewsSlug,
    setActiveNewsSlug,
  ] = useState(
    initial.newsSlug
  );

  const [
    forumTarget,
    setForumTarget,
  ] = useState({
    cat: initial.forumCat || null,
    subcat: initial.forumSubcat || null,
    thread: initial.forumThread || null,
  });

  // Incremented whenever a global control opens the forum so a fresh root
  // navigation cannot reuse stale component-local UI state.
  const [forumNavKey, setForumNavKey] = useState(0);

  // Home section to scroll to after navigating back from another page.
  const [pendingHomeAnchor, setPendingHomeAnchor] = useState(null);

  const navRef = useRef(null);

  const [
    heroHeight,
    setHeroHeight,
  ] = useState("100vh");

  useEffect(() => {
    const onPop = () => {
      const {
        page: p,
        tab: t,
        modal: m,
        newsSlug: s,
        forumCat: fc,
        forumSubcat: fs,
        forumThread: ft,
      } = parsePath();

      setPage(p);

      if (p === "forum") {
        setForumTarget({
          cat: fc || null,
          subcat: fs || null,
          thread: ft || null,
        });
      }

      if (
        p === "mcstore" &&
        t
      ) {
        setMcStoreTab(t);
      }

      if (
        p === "minecraft" &&
        t
      ) {
        setMcPageTab(t);
      }

      setStoreModal(
        m === "store"
      );

      setActiveNewsSlug(s);
    };

    window.addEventListener(
      "popstate",
      onPop
    );

    return () =>
      window.removeEventListener(
        "popstate",
        onPop
      );
  }, []);

  const fetchNews = () => {
    fetch(
      `${API}/news/home`
    )
      .then((r) => r.json())
      .then((d) =>
        setNewsPosts(
          d.posts || []
        )
      )
      .catch(() =>
        setNewsPosts([])
      )
      .finally(() =>
        setNewsLoading(false)
      );
  };

  useEffect(
    fetchNews,
    []
  );

  useEffect(() => {
    const calc = () => {
      const nH =
        navRef.current
          ?.offsetHeight || 62;

      setHeroHeight(
        `calc(100vh - ${nH}px)`
      );
    };

    calc();

    window.addEventListener(
      "resize",
      calc
    );

    return () =>
      window.removeEventListener(
        "resize",
        calc
      );
  }, []);

  useEffect(() => {
    // Normal page changes start at the top. When a navbar item is targeting
    // a section on Home, let the dedicated anchor effect handle scrolling.
    // IMPORTANT: only depend on `page` here. If `pendingHomeAnchor` were also
    // a dependency, clearing it after a successful section scroll would run
    // this effect again and immediately jump the user back to the top.
    if (!pendingHomeAnchor) {
      window.scrollTo(
        0,
        0
      );
    }
  }, [page]);

  useEffect(() => {
    if (page !== "home" || !pendingHomeAnchor) return;

    // Wait until the Home sections have mounted before trying to find them.
    const frame = requestAnimationFrame(() => {
      const target = document.getElementById(pendingHomeAnchor);

      if (target) {
        target.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }

      setPendingHomeAnchor(null);
    });

    return () => cancelAnimationFrame(frame);
  }, [page, pendingHomeAnchor]);

  const openStorePicker =
    useCallback(() => {
      if (isForumDomain()) {
        window.location.assign(`${MAIN_ORIGIN}/store`);
        return;
      }
      nav("/store");
      setStoreModal(true);
    }, []);

  const openMcStore =
    useCallback(
      (
        tab = "ranks"
      ) => {
        const path = tab === "ranks" ? "/store" : `/store/${tab}`;

        if (!isMinecraftDomain()) {
          window.location.assign(`${MINECRAFT_ORIGIN}${path}`);
          return;
        }

        nav(path);
        setStoreModal(false);
        setMcStoreTab(tab);
        setPage("mcstore");
      },
      []
    );

  const goHome =
    useCallback(() => {
      if (isForumDomain() || isMinecraftDomain() || isStoreDomain()) {
        window.location.assign(`${MAIN_ORIGIN}/`);
        return;
      }
      nav("/");
      setPendingHomeAnchor(null);
      setActiveNewsSlug(null);
      setPage("home");
      setStoreModal(false);
    }, []);

  const goCommunity =
    useCallback(() => {
      if (isForumDomain() || isMinecraftDomain() || isStoreDomain()) {
        window.location.assign(`${MAIN_ORIGIN}/#features`);
        return;
      }
      nav("/#features");
      setStoreModal(false);
      setActiveNewsSlug(null);
      setPendingHomeAnchor("features");
      setPage("home");
    }, []);

  const goMinecraft =
    useCallback(() => {
      if (!isMinecraftDomain()) {
        window.location.assign(`${MINECRAFT_ORIGIN}/`);
        return;
      }
      nav("/");
      setMcPageTab("about");
      setPage("minecraft");
    }, []);

  const goMinecraftApply =
    useCallback(() => {
      if (!isMinecraftDomain()) {
        window.location.assign(`${MINECRAFT_ORIGIN}/apply`);
        return;
      }
      nav("/apply");
      setPage("mcapply");
    }, []);

  const closeStoreModal =
    useCallback(() => {
      setStoreModal(false);
    }, []);

  const openNewsPost =
    useCallback(
      (slug) => {
        nav(
          `/news/${slug}`
        );

        setActiveNewsSlug(
          slug
        );
      },
      []
    );

  const closeNewsPost =
    useCallback(() => {
      nav("/");
      setActiveNewsSlug(null);
    }, []);

  const openForum =
    useCallback(
      (
        cat = null,
        subcat = null,
        thread = null
      ) => {
        const path = forumPath(cat, subcat, thread);

        // Crossing from the main site to the forum requires a real navigation
        // because history.pushState cannot change origins.
        if (!isForumDomain()) {
          window.location.assign(`${FORUM_ORIGIN}${path}`);
          return;
        }

        nav(path);
        setForumTarget({ cat, subcat, thread });
        setForumNavKey((k) => k + 1);
        setPage("forum");
      },
      []
    );

  const goCharity =
    useCallback(() => {
      if (isForumDomain() || isMinecraftDomain() || isStoreDomain()) {
        window.location.assign(`${MAIN_ORIGIN}/charity-fund`);
        return;
      }
      nav("/charity-fund");
      setStoreModal(false);
      setActiveNewsSlug(null);
      setPage("charity");
    }, []);

  const goMerch =
    useCallback(() => {
      if (!isStoreDomain()) {
        window.location.assign(`${STORE_ORIGIN}/`);
        return;
      }
      nav("/");
      setPage("merch");
    }, []);

  if (authLoading) {
    return null;
  }

  const copyIP = () => {
    navigator.clipboard
      .writeText(
        "play.theonesabove.com"
      )
      .then(() =>
        showToast(
          "IP Copied: play.theonesabove.com"
        )
      )
      .catch(() =>
        showToast(
          "Server: play.theonesabove.com"
        )
      );
  };

  return (
    <>
      {page !== "merch" && page !== "minecraft" && page !== "mcapply" && (
        <Navbar
          navRef={navRef}
          onOpenModal={
            setAuthModal
          }
          onOpenStore={
            openStorePicker
          }
          onOpenAdmin={() =>
            setAdminOpen(true)
          }
          onOpenSettings={() =>
            setSettingsOpen(
              true
            )
          }
          cartCount={cart.reduce(
            (s, i) =>
              s + i.qty,
            0
          )}
          currentPage={page}
          onGoHome={goHome}
          onGoCommunity={goCommunity}
          onOpenMinecraft={goMinecraft}
          onOpenForum={openForum}
          onOpenCharity={goCharity}
        />
      )}

      {page === "home" && (
        <>
          <Hero
            heroHeight={
              heroHeight
            }
            onOpenStore={
              openStorePicker
            }
            onCopyIP={
              copyIP
            }
            onOpenForum={
              openForum
            }
          />

          <div className="toa-main-outer">
            <div className="toa-main">
              <NewsFeed
                posts={
                  newsPosts
                }
                loading={
                  newsLoading
                }
                onOpenAdminPost={() =>
                  setAdminOpen(
                    true
                  )
                }
                onOpenPost={
                  openNewsPost
                }
              />

              <Sidebar
                onOpenForum={
                  openForum
                }
                onOpenMerch={
                  goMerch
                }
              />
            </div>
          </div>

          <FeaturesSection />

          <PodcastSection />

          <MinecraftSection
            onCopyIP={
              copyIP
            }
            onOpenStore={
              openStorePicker
            }
            onOpenMcStore={
              openMcStore
            }
            onOpenMcPage={() => {
              window.location.assign(`${MINECRAFT_ORIGIN}/`);
            }}
          />

          <DiscordSection />

          <Footer />
        </>
      )}

      {page === "charity" && (
        <>
          <CharityFundPage />
          <Footer />
        </>
      )}

      {page === "mcstore" && (
        <>
          <MinecraftStorePage
            initialTab={mcStoreTab}
            onClose={goMinecraft}
            onOpenAuth={setAuthModal}
          />
          <Footer />
        </>
      )}

      {page === "forum" && (
        <>
          <ForumPage
            key={`forum-${forumNavKey}`}
            showToast={showToast}
            onGoHome={goHome}
            initialCat={forumTarget.cat}
            initialSubcat={forumTarget.subcat}
            initialThread={forumTarget.thread}
          />
          <Footer />
        </>
      )}

      {page === "mcapply" && (
        <MinecraftApplicationPage onBack={goMinecraft} onOpenAuth={setAuthModal} />
      )}

      {page === "minecraft" && (
        <>
        <MinecraftPage
          initialTab={
            mcPageTab
          }
          onTabChange={(
            id
          ) => {
            setMcPageTab(
              id
            );

            window.history.replaceState(
              null,
              "",
              isMinecraftDomain()
                ? (id === "about" ? "/" : `/${id}`)
                : (id === "about" ? "/minecraft" : `/minecraft/${id}`)
            );
          }}
          onClose={
            goHome
          }
          onOpenMcStore={
            openMcStore
          }
          onCopyIP={
            copyIP
          }
          showToast={
            showToast
          }
          onOpenForum={
            openForum
          }
          onOpenAuth={setAuthModal}
          onOpenSettings={() => setSettingsOpen(true)}
          onOpenAdmin={() => setAdminOpen(true)}
          onApply={goMinecraftApply}
        />
        <Footer />
        </>
      )}

      {page === "merch" && (
        <ClothingStorePage
          onGoHome={goHome}
          authUser={authUser}
          accessToken={accessToken}
          onOpenAuth={
            setAuthModal
          }
          onOpenSettings={() =>
            setSettingsOpen(
              true
            )
          }
          onLogout={logout}
        />
      )}

      <Toast toast={toast} />

      {storeModal && (
        <StoreModal
          onClose={
            closeStoreModal
          }
          showToast={
            showToast
          }
          onOpenMcStore={
            openMcStore
          }
        />
      )}

      {authModal && (
        <AuthModal
          modal={
            authModal
          }
          onClose={() =>
            setAuthModal(
              null
            )
          }
          showToast={
            showToast
          }
        />
      )}

      {settingsOpen && (
        <SettingsModal
          onClose={() =>
            setSettingsOpen(
              false
            )
          }
          showToast={
            showToast
          }
        />
      )}

      {activeNewsSlug && (
        <NewsPostModal
          post={
            newsPosts.find(
              (p) =>
                p.slug ===
                activeNewsSlug
            ) || {
              slug:
                activeNewsSlug,
            }
          }
          onClose={
            closeNewsPost
          }
        />
      )}

      {adminOpen &&
        canAccessDashboard(authUser?.role) && (
          <AdminDashboard
            onClose={() => {
              setAdminOpen(
                false
              );

              fetchNews();
            }}
            showToast={
              showToast
            }
          />
        )}
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppInner />
    </AuthProvider>
  );
}